/* Calzado Audor · la fila que se desliza
   Una sola pieza para las dos bandas que la usan: «Cómo se compra aquí» y cualquier otra
   que quiera tarjetas en horizontal. Los recomendados NO la usan: ese carrusel es nativo
   de Horizon (`layout_type: carousel`) y se enciende desde un ajuste (SOP §9.17.3).

   NO HAY LIBRERÍA Y NO HACE FALTA. El desplazamiento es `scroll-snap` del navegador, que
   ya funciona con el dedo, con la rueda y con el teclado. Esto solo añade las flechas, los
   puntos y el avance solo.

   EL AVANCE SOLO SE RINDE ANTE EL COMPRADOR. Se para en cuanto alguien pasa el mouse, toca
   o entra con el teclado, y no arranca si el sistema pide menos movimiento. Un carrusel que
   se mueve mientras alguien lee es una trampa, no una ayuda.

   LA SOMBRA NO SE RECORTA. `overflow-x: auto` obliga al eje vertical a recortar también, así
   que el carril lleva relleno propio en el CSS para que la sombra de la tarjeta quepa entera.
   Eso vive en la hoja de estilo de cada sección, no aquí. */

const CALMA = window.matchMedia('(prefers-reduced-motion: reduce)');

class AudCarrusel extends HTMLElement {
  connectedCallback() {
    this.pista = this.querySelector('[data-aud-pista]');
    this.puntos = this.querySelector('[data-aud-puntos]');
    this.flechas = [...this.querySelectorAll('[data-aud-ir]')];
    if (!this.pista) return;

    this.solo = Number(this.dataset.solo || 0);
    this.quieto = false;
    this.temporizador = null;

    this.sembrarPuntos();
    this.pista.addEventListener('scroll', this.alDesplazar, { passive: true });
    for (const f of this.flechas) f.addEventListener('click', this.alPulsarFlecha);
    this.puntos?.addEventListener('click', this.alPulsarPunto);

    for (const ev of ['pointerenter', 'pointerdown', 'focusin']) {
      this.addEventListener(ev, this.detener);
    }
    this.addEventListener('pointerleave', this.reanudar);

    /* La ventana cambia de ancho y con ella cuántas tarjetas caben. Se observan también las
       tarjetas: al conectar todavía miden su ancho de celular (82 %) y la pista parece
       desbordada; cuando la hoja les da sus 320 px la pista no cambia de tamaño y, sin esto,
       nadie volvía a medir y la fila se quedaba con flechas y sin centrar. */
    this.observador = new ResizeObserver(() => this.pintar());
    this.observador.observe(this.pista);
    for (const t of this.tarjetas) this.observador.observe(t);

    this.arrancar();
    requestAnimationFrame(() => this.pintar());
    /* El texto con la fuente de respaldo es más ancho y desborda la tarjeta sin cambiarle el
       tamaño: el observador no se entera cuando llega Barlow. Se vuelve a medir al final. */
    document.fonts?.ready.then(() => this.pintar());
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => this.pintar(), { once: true });
    }
  }

  disconnectedCallback() {
    this.detener();
    this.observador?.disconnect();
    this.pista?.removeEventListener('scroll', this.alDesplazar);
  }

  get tarjetas() {
    return [...(this.pista?.children || [])];
  }

  sembrarPuntos() {
    if (!this.puntos) return;
    this.puntos.textContent = '';
    this.tarjetas.forEach((_, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'aud-carr__punto';
      b.dataset.i = String(i);
      b.setAttribute('aria-label', `${this.dataset.rotuloPunto || 'Tarjeta'} ${i + 1}`);
      if (i === 0) b.setAttribute('aria-current', 'true');
      this.puntos.appendChild(b);
    });
  }

  /* Dónde queda cada tarjeta medida desde la PRIMERA, no desde el borde del carril: así el
     relleno del carril no cuenta y la primera tarjeta vive en 0, que es donde arranca la fila. */
  posicion(t) {
    return t.offsetLeft - (this.tarjetas[0]?.offsetLeft || 0);
  }

  /* Cuál está más cerca del inicio de la fila. No se calcula con el índice porque las
     tarjetas no miden todas lo mismo y el ancho cambia con la ventana. */
  actual() {
    let mejor = 0;
    let dist = Infinity;
    this.tarjetas.forEach((t, i) => {
      const d = Math.abs(this.posicion(t) - this.pista.scrollLeft);
      if (d < dist) {
        dist = d;
        mejor = i;
      }
    });
    return mejor;
  }

  irA(i) {
    const ts = this.tarjetas;
    const t = ts[Math.max(0, Math.min(ts.length - 1, i))];
    if (!t) return;
    this.pista.scrollTo({
      left: this.posicion(t),
      behavior: CALMA.matches ? 'auto' : 'smooth',
    });
  }

  pintar() {
    if (!this.pista) return;
    const a = this.actual();
    for (const p of this.puntos?.children || []) {
      if (Number(p.dataset.i) === a) p.setAttribute('aria-current', 'true');
      else p.removeAttribute('aria-current');
    }
    const fin = this.pista.scrollWidth - this.pista.clientWidth;
    const ultima = this.tarjetas.length - 1;
    /* Se apagan del todo en la punta de la fila: la fila no da la vuelta, así que en la
       primera tarjeta no hay nada a la izquierda y en la última nada a la derecha. */
    if (this.flechas[0]) this.flechas[0].disabled = a === 0 || this.pista.scrollLeft <= 2;
    if (this.flechas[1]) this.flechas[1].disabled = a === ultima || this.pista.scrollLeft >= fin - 2;
    /* Sin nada que desplazar no hay mandos que enseñar. */
    this.classList.toggle('is-quieta', fin <= 2);
  }

  alDesplazar = () => {
    clearTimeout(this.pintado);
    this.pintado = setTimeout(() => this.pintar(), 90);
  };

  alPulsarFlecha = (e) => {
    this.detener();
    this.irA(this.actual() + Number(e.currentTarget.dataset.audIr));
  };

  alPulsarPunto = (e) => {
    const b = e.target.closest('.aud-carr__punto');
    if (!b) return;
    this.detener();
    this.irA(Number(b.dataset.i));
  };

  arrancar = () => {
    if (!this.solo || CALMA.matches || this.quieto) return;
    clearInterval(this.temporizador);
    this.temporizador = setInterval(() => {
      const ts = this.tarjetas;
      const a = this.actual();
      this.irA(a >= ts.length - 1 ? 0 : a + 1);
    }, this.solo);
  };

  detener = () => {
    this.quieto = true;
    clearInterval(this.temporizador);
    this.temporizador = null;
  };

  reanudar = () => {
    this.quieto = false;
    this.arrancar();
  };
}

if (!customElements.get('aud-carrusel')) customElements.define('aud-carrusel', AudCarrusel);
